import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho yêu cầu đăng nhập
 */
export interface LoginRequest {
  /** Email của người dùng */
  email: string;
  /** Mật khẩu của người dùng */
  password: string;
}

/**
 * Interface cho phản hồi đăng nhập
 */
export interface LoginResponse {
  /** Token truy cập JWT */
  access_token: string;
  /** Thông tin người dùng */
  user: {
    /** ID duy nhất của người dùng */
    id: string;
    /** Email của người dùng */
    email: string;
    /** Tên đầy đủ của người dùng */
    fullName: string;
    /** Vai trò của người dùng trong hệ thống */
    role: 'GUEST' | 'MEMBER' | 'TOOL_USER' | 'PREMIUM' | 'MODERATOR' | 'ADMIN';
    /** Trạng thái hoạt động của tài khoản */
    isActive: boolean;
    /** Số token còn lại (chỉ áp dụng cho tài khoản PREMIUM) */
    tokens?: number;
    /** Loại gói dịch vụ của người dùng */
    packageType?: 'FREE' | 'BASIC' | 'PREMIUM' | 'ADVANCED' | 'SUPER';
  };
}

/**
 * Interface cho yêu cầu đăng ký tài khoản
 */
export interface RegisterRequest {
  /** Tên đầy đủ của người dùng */
  fullName: string;
  /** Email của người dùng */
  email: string;
  /** Mật khẩu của người dùng */
  password: string;
  /** Xác nhận mật khẩu */
  confirmPassword: string;
  /** Mã xác thực email */
  verificationCode: string;
  /** Số điện thoại (tùy chọn) */
  phone?: string;
  /** Giới tính (tùy chọn) */
  gender?: 'male' | 'female' | 'other';
  /** Ngày sinh (tùy chọn) */
  dateOfBirth?: string;
  /** Xác nhận đã chấp nhận điều khoản sử dụng */
  termsAccepted: boolean;
}

/**
 * Interface cho yêu cầu quên mật khẩu
 */
export interface ForgotPasswordRequest {
  /** Email của người dùng cần đặt lại mật khẩu */
  email: string;
}

/**
 * Interface cho yêu cầu đặt lại mật khẩu
 */
export interface ResetPasswordRequest {
  /** Email của người dùng */
  email: string;
  /** Token xác thực để đặt lại mật khẩu */
  token: string;
  /** Mật khẩu mới */
  newPassword: string;
  /** Xác nhận mật khẩu mới */
  confirmPassword: string;
}

/**
 * Interface cho yêu cầu xác thực email
 */
export interface VerifyEmailRequest {
  /** Token xác thực email */
  token: string;
  /** Email cần xác thực */
  email: string;
}

/**
 * Interface cho yêu cầu gửi lại email xác thực
 */
export interface ResendVerificationRequest {
  /** Email cần gửi lại mã xác thực */
  email: string;
}

/**
 * Service xử lý các chức năng xác thực người dùng
 * Bao gồm đăng nhập, đăng ký, quên mật khẩu, xác thực email
 */
export class AuthService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /**
   * Đăng nhập người dùng vào hệ thống
   * @param data - Thông tin đăng nhập (email và mật khẩu)
   * @returns Promise<LoginResponse> - Thông tin người dùng và token truy cập
   * @throws Error - Khi đăng nhập thất bại
   */
  static async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Đăng nhập thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    return response.json();
  }

  /**
   * Đăng ký tài khoản người dùng mới
   * @param data - Thông tin đăng ký tài khoản
   * @returns Promise<any> - Kết quả đăng ký
   * @throws Error - Khi đăng ký thất bại
   */
  static async register(data: RegisterRequest): Promise<any> {
    const response = await fetch(`${this.baseUrl}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Đăng ký thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    return response.json();
  }

  /**
   * Gửi email đặt lại mật khẩu
   * @param data - Thông tin email cần đặt lại mật khẩu
   * @returns Promise<void>
   * @throws Error - Khi gửi email thất bại
   */
  static async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Gửi email thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }

  /**
   * Đặt lại mật khẩu với token xác thực
   * @param data - Thông tin đặt lại mật khẩu (email, token, mật khẩu mới)
   * @returns Promise<void>
   * @throws Error - Khi đặt lại mật khẩu thất bại
   */
  static async resetPassword(data: ResetPasswordRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Đặt lại mật khẩu thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }

  /**
   * Xác thực email với token
   * @param data - Thông tin xác thực (token và email)
   * @returns Promise<void>
   * @throws Error - Khi xác thực email thất bại
   */
  static async verifyEmail(data: VerifyEmailRequest): Promise<void> {
    const url = `${this.baseUrl}/auth/verify?token=${encodeURIComponent(data.token)}&email=${encodeURIComponent(data.email)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xác thực email thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }

  /**
   * Gửi lại email xác thực
   * @param data - Thông tin email cần gửi lại mã xác thực
   * @returns Promise<void>
   * @throws Error - Khi gửi lại email xác thực thất bại
   */
  static async resendVerification(data: ResendVerificationRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Gửi lại email xác thực thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }

  /**
   * Lấy thông tin user hiện tại
   * @returns Promise<LoginResponse['user']> - Thông tin user hiện tại
   * @throws Error - Khi không thể lấy thông tin user
   */
  static async getCurrentUser(): Promise<LoginResponse['user']> {
    const timestamp = Date.now();
    const response = await apiClient.get(`/auth/me?t=${timestamp}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể lấy thông tin user';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    const data = await response.json();
    return data.user;
  }
  
  /**
   * Đổi mật khẩu cho người dùng hiện tại
   */
  static async changePassword(data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<void> {
    const response = await apiClient.post('/auth/change-password', data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Đổi mật khẩu thất bại!';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }
  
  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }
}
